import * as React from 'react';
import { useCallback, useEffect, useState } from 'react';
import { Box, Grid, LinearProgress } from '@mui/material';
import { LayoutDashboard } from '../../../shared/layouts';
import { GenericService } from '../../../shared/services';
import { DefectDTO, DefectFilterDTO, PeopleDTO, PeopleFilterDTO, ValueListDTO } from '../../../shared/interfaces';
import { ISnackbarAlert, SnackbarAlert, CardList, TextFieldInput, ToolBar, IDialogActions, ICardActions, ICardColumns, IChipFilter, SelectField } from '../../../shared/components';
import { DefectMaintenance } from './DefectMaintenance';
import { Delete } from '../../../shared/components/confirm-delete/ConfirmDelete';
import { ListId, LocalStorange } from '../../../shared/environment';

export const Defect: React.FC = () => {

    const [createdByList, setCreatedBy] = useState<PeopleDTO[]>([]);
    const [assignToList, setAssignToList] = useState<PeopleDTO[]>([]);
    const [statusList, setStatusList] = useState<ValueListDTO[]>([]);
    const [priorityList, setPriorityList] = useState<ValueListDTO[]>([]);
    const [severityList, setSeverityList] = useState<ValueListDTO[]>([]);
    const [typeList, setTypeList] = useState<ValueListDTO[]>([]);
    const [defectList, setDefectList] = useState<DefectDTO[]>([]);
    const [defectFilterDTO, setDefectFilterDTO] = useState<DefectFilterDTO>();

    const [actionExecuted, setActionExecuted] = useState(false);
    const [newRecord, setNewRecord] = useState('');
    const [editRecord, setEditRecord] = useState('');
    const [deleteRecord, setDeleteRecord] = useState('');
    const [expadedData, setExpandedData] = useState('');
    const [isOpenMaintenance, setIsOpenMaintenance] = useState(false);
    const [selectRecord, setSelectRecord] = useState('');

    const [loading, setLoading] = useState(false);
    const [snackBarMessage, setSnackBarMessage] = useState<ISnackbarAlert>();
    const [isOpenSnackBar, setIsOpenSnackBar] = useState(false);

    const userInfo = JSON.parse(localStorage.getItem(LocalStorange.USERINFO) || '');

    useEffect(() => {
        setDefectFilterDTO({
            ...defectFilterDTO,
            name: '',
            code: '',
            createdById: '',
            assignToId: '',
            statusId: '',
            priorityId: '',
            severityId: '',
            typeId: ''
        } as DefectFilterDTO);
    }, []);

    useEffect(() => {
        handleDefectGetAllViewAsync();

        handleListGetAll(ListId.testingsphere.defect.statusId, setStatusList);
        handleListGetAll(ListId.testingsphere.defect.priorityId, setPriorityList);
        handleListGetAll(ListId.testingsphere.defect.severityId, setSeverityList);
        handleListGetAll(ListId.testingsphere.defect.typeId, setTypeList);
        handlePeopleGetAll(setCreatedBy);
        handlePeopleGetAll(setAssignToList);
    }, [defectFilterDTO]);

    useEffect(() => {
        if (actionExecuted)
            handleDefectGetAllViewAsync();
    }, [actionExecuted]);

    const handleListGetAll = useCallback(async (listId: string, setList: any) => {
        const valueListFilterDTO = {
            value: '',
            listId: listId,
            orderBy: 'valueIndex ASC'
        };
        GenericService.GetAllAsync1(
            'parmsphere.valueList',
            valueListFilterDTO,
            setLoading,
            setList,
            showMessage
        );
    }, []);

    const handlePeopleGetAll = useCallback(async (setList: any) => {
        const peopleFilterDTO: PeopleFilterDTO = {
            companyId: userInfo.user.companyId,
            orderBy: 'name ASC'
        };
        GenericService.GetAllAsync1('corpsphere.people', peopleFilterDTO, setLoading, setList, showMessage);
    }, []);
    
    const handleDefectGetAllViewAsync = useCallback(async () => {
        if (defectFilterDTO === undefined)
            return;

        GenericService.GetAllViewAsync1('testingsphere.defect', defectFilterDTO, setActionExecuted, setLoading, setDefectList, showMessage, true);
    }, [defectFilterDTO]);

    const handleClearFilterClick = () => {
        setDefectFilterDTO({
            ...defectFilterDTO,
            name: '',
            code: '',
            createdById: '',
            assignToId: '',
            statusId: '',
            priorityId: '',
            severityId: '',
            typeId:''
        } as DefectFilterDTO);
    };

    function handleSearchClick() {
        handleDefectGetAllViewAsync();
    }

    const handleNew = () => {
        setNewRecord('data');
        setEditRecord('');
        setDeleteRecord('');
        setSelectRecord('');
        setIsOpenMaintenance(true);
    };

    const handleViewDetatils = (data: string) => {
        setNewRecord('');
        setEditRecord(data);
        setDeleteRecord('');
        setSelectRecord('');
        setIsOpenMaintenance(true);
    };

    const handleDelete = (data: string) => {
        setNewRecord('');
        setDeleteRecord(data);
        setEditRecord('');
        setSelectRecord('');
        setIsOpenMaintenance(true);
    };

    const handleAssociateUsers = (data: string) => {
        setNewRecord('');
        setDeleteRecord('');
        setEditRecord('');
        setSelectRecord(data);
        setIsOpenMaintenance(true);
    };

    const toolBarActions: IDialogActions[] = [
        { id: 'new', text: 'New Defect', icon: 'add_circle_outlined', iconColor: 'primary', setAction: handleNew },
    ];

    const cardActions: ICardActions[] = [
        { id: 'viewDetails', toolTip: 'View Details', icon: 'preview_outlined', iconColor: 'primary', setAction: handleViewDetatils },
        { id: 'delete', toolTip: 'Delete', icon: 'delete_outlined', iconColor: 'error', setAction: handleDelete },
    ];

    const chipFilters: IChipFilter[] = [
        { label: 'Total Records', value: defectList.length },
        { label: 'Name', value: defectFilterDTO?.name },
        { label: 'Code', value: defectFilterDTO?.code },
        { label: 'Status', value: statusList.find(x => x.id === defectFilterDTO?.statusId)?.value },
        { label: 'Priority', value: priorityList.find(x => x.id === defectFilterDTO?.priorityId)?.value },
        { label: 'Severity', value: severityList.find(x => x.id === defectFilterDTO?.severityId)?.value },
        { label: 'Type', value: typeList.find(x => x.id === defectFilterDTO?.typeId)?.value },
    ];

    const columns: ICardColumns[] = [
        { field: 'code', label: 'Code', size: 2, type: 'text', visible: true, isChip: true, hasFieldLine: false, hasToolTip: true },
        { field: 'name', label: 'Name', size: 4, type: 'text', visible: true, isChip: false, hasFieldLine: false, hasToolTip: true },
        { field: 'createdBy', label: 'Created By', size: 2, type: 'text', visible: true, isChip: false, hasFieldLine: false, hasToolTip: true },
        { field: 'assignTo', label: 'Assign To', size: 2, type: 'text', visible: true, isChip: false, hasFieldLine: false, hasToolTip: true },
        { field: 'status', label: 'Status', size: 2, type: 'text', visible: true, isChip: true, hasFieldLine: false, hasToolTip: true },
        { field: 'priority', label: 'Priority', size: 2, type: 'text', visible: true, isChip: false, hasFieldLine: false, hasToolTip: true },
        { field: 'severity', label: 'Severity', size: 2, type: 'text', visible: true, isChip: false, hasFieldLine: false, hasToolTip: true },
        { field: 'type', label: 'Type', size: 2, type: 'text', visible: true, isChip: false, hasFieldLine: false, hasToolTip: true },
        { field: 'application', label: 'Application', size: 2, type: 'text', visible: true, isChip: false, hasFieldLine: false, hasToolTip: true },
        { field: 'feature', label: 'Feature', size: 2, type: 'text', visible: true, isChip: false, hasFieldLine: false, hasToolTip: true },
        { field: 'workspace', label: 'Workspace', size: 2, type: 'text', visible: true, isChip: false, hasFieldLine: false, hasToolTip: true },
    ];

    function showMessage(message: string, type: 'error' | 'info' | 'success' | 'warning') {
        const msg: ISnackbarAlert = {
            id: 'message',
            message: message,
            type: type,
        };
        setSnackBarMessage(msg);
        setIsOpenSnackBar(true);
    }

    return (
        <React.Fragment>
            {isOpenSnackBar && (
                <SnackbarAlert
                    snackBarAlert={snackBarMessage}
                    setIsOpen={setIsOpenSnackBar}
                    isOpen={isOpenSnackBar}
                />
            )}
            <LayoutDashboard title='DEFECT'>
                <ToolBar
                    clickSearch={handleSearchClick}
                    clickClearFilter={handleClearFilterClick}
                    chipFilters={chipFilters}
                    actions={toolBarActions}
                >
                    <Grid container spacing={1}>
                        <Grid item xs={12} md={3}>
                            <TextFieldInput
                                id='name'
                                label='Name'
                                setValue={setDefectFilterDTO}
                                value={defectFilterDTO?.name}
                                obj={defectFilterDTO}
                                placeholder={'Name'}
                                maxLength={200}
                            />
                        </Grid>
                        <Grid item xs={12} md={3}>
                            <TextFieldInput
                                id='code'
                                label='Code'
                                setValue={setDefectFilterDTO}
                                value={defectFilterDTO?.code}
                                obj={defectFilterDTO}
                                placeholder={'Code'}
                                maxLength={200}
                            />
                        </Grid>
                        <Grid item xs={12} md={3}>
                            <SelectField
                                id='createdById'
                                label='Created By'
                                setValue={setDefectFilterDTO}
                                value={defectFilterDTO?.createdById}
                                obj={defectFilterDTO}
                                valueList={createdByList}
                                hasNoneSelect={true}
                            />
                        </Grid>
                        <Grid item xs={12} md={3}>
                            <SelectField
                                id='assignToId'
                                label='Assign To'
                                setValue={setDefectFilterDTO}
                                value={defectFilterDTO?.assignToId}
                                obj={defectFilterDTO}
                                valueList={assignToList}
                                hasNoneSelect={true}
                            />
                        </Grid>
                        <Grid item xs={12} md={3}>
                            <SelectField
                                id='typeId'
                                label='Type'
                                setValue={setDefectFilterDTO}
                                value={defectFilterDTO?.typeId}
                                obj={defectFilterDTO}
                                valueList={typeList}
                                hasNoneSelect={true}
                            />
                        </Grid>
                        <Grid item xs={12} md={3}>
                            <SelectField
                                id='statusId'
                                label='Status'
                                setValue={setDefectFilterDTO}
                                value={defectFilterDTO?.statusId}
                                obj={defectFilterDTO}
                                valueList={statusList}
                                hasNoneSelect={true}
                            />
                        </Grid>
                        <Grid item xs={12} md={3}>
                            <SelectField
                                id='priorityId'
                                label='Priority'
                                setValue={setDefectFilterDTO}
                                value={defectFilterDTO?.priorityId}
                                obj={defectFilterDTO}
                                valueList={priorityList}
                                hasNoneSelect={true}
                            />
                        </Grid>
                        <Grid item xs={12} md={3}>
                            <SelectField
                                id='severityId'
                                label='Severity'
                                setValue={setDefectFilterDTO}
                                value={defectFilterDTO?.severityId}
                                obj={defectFilterDTO}
                                valueList={severityList}
                                hasNoneSelect={true}
                            />
                        </Grid>
                    </Grid>
                </ToolBar>
                {loading && (
                    <LinearProgress sx={{ borderRadius: 3, mt: - 0.7, mb: 0.2 }} />
                )}
                <Box sx={{ display: 'flex', maxHeight: '79vh', width: '100%', overflow: 'hidden', overflowY: 'auto' }}>
                    <CardList
                        columns={columns}
                        rows={defectList}
                        actions={cardActions}
                        hasChildren={false}
                        setRowExpanded={setExpandedData}
                        cardSize={12}
                    />
                </Box>
                {newRecord && (
                    <DefectMaintenance
                        data={''}
                        setIsOpenMaintenance={setIsOpenMaintenance}
                        isOpenMaintenance={isOpenMaintenance}
                        setActionExecuted={setActionExecuted}
                    />
                )}
                {editRecord && (
                    <DefectMaintenance
                        data={editRecord}
                        setIsOpenMaintenance={setIsOpenMaintenance}
                        isOpenMaintenance={isOpenMaintenance}
                        setActionExecuted={setActionExecuted}
                    />
                )}
                {deleteRecord && (<Delete
                    entity={'testingsphere.defect'}
                    data={deleteRecord}
                    setIsOpenDiaglog={setIsOpenMaintenance}
                    isOpenDialog={isOpenMaintenance}
                    setActionExecuted={setActionExecuted}
                />
                )}
            </LayoutDashboard>
        </React.Fragment >
    );
};